<script setup>
import {
  ref,
  computed,
  onBeforeMount,
  onMounted,
  onUnmounted,
  defineEmits,
  defineProps,
} from "vue"
import { useI18n } from "vue-i18n"
import HardwareProductSearch from "./HardwareProductSearch.vue"
import Banner from "./Banner.vue"
import SecondaryMenubar from "./SecondaryMenubar.vue"

const props = defineProps({
  dark: Boolean,
})

const emit = defineEmits(["sign-up", "sign-in"])

const { t } = useI18n()

const top = ref(false)

const preloading = ref(true)
const featuredManufacturers = ref([])
const primaryNavigation = ref([])

onMounted(() => {
  document.addEventListener("scroll", handleScroll())
})
onUnmounted(() => {
  document.removeEventListener("scroll", handleScroll())
})
function handleScroll() {
  top.value = window.pageYOffset > 10 ? false : true
}

onBeforeMount(async () => {
  try {
    const response = await axios.get(`/hardware/manufacturers/search`, {
      params: {
        hasProductListings: true,
        featured: true,
        disablePagination: true,
      },
    })
    featuredManufacturers.value = response.data.items

    primaryNavigation.value = buildPrimaryNavigation()
  } finally {
    preloading.value = false
  }
})

const collections = computed(() => {
  return {
    panels: {
      id: "PANELS",
      sections: [
        {
          name: t("common.shop.hardware.navigation.primary.manufacturers"),
          items: featuredManufacturers.value
            .filter((o) => o.hasSolarPanels)
            .map((o) => {
              return {
                name: o.companyName,
                to: {
                  name: "HardwareMarketplaceCollectionManufacturer",

                  params: { collection: "panels", manufacturerId: o.slug },
                },
              }
            }),
        },
        {
          name: t("common.shop.hardware.navigation.primary.article-groups"),
          items: [],
        },
      ],
    },
    inverters: {
      id: "INVERTERS",
      sections: [
        {
          name: t("common.shop.hardware.navigation.primary.manufacturers"),
          items: featuredManufacturers.value
            .filter((o) => o.hasSolarInverters)
            .map((o) => {
              return {
                name: o.companyName,
                to: {
                  name: "HardwareMarketplaceCollectionManufacturer",

                  params: { collection: "inverters", manufacturerId: o.slug },
                },
              }
            }),
        },
        {
          name: t("common.shop.hardware.navigation.primary.article-groups"),
          items: [
            {
              name: t("common.api.products.collections.INVERTERS_MONOPHASE"),
              to: {
                name: "HardwareMarketplaceCollection",
                params: { collection: "inverters" },
              },
            },
            {
              name: t(
                "common.api.products.collections.INVERTERS_TRIPHASE_LTE_30KW",
              ),
              to: {
                name: "HardwareMarketplaceCollection",
                params: { collection: "inverters" },
              },
            },
            {
              name: t(
                "common.api.products.collections.INVERTERS_TRIPHASE_GT_30KW",
              ),
              to: {
                name: "HardwareMarketplaceCollection",
                params: { collection: "inverters" },
              },
            },
            {
              name: t("common.api.products.collections.INVERTERS_HYBRID"),
              to: {
                name: "HardwareMarketplaceCollection",
                params: { collection: "inverters" },
              },
            },
            {
              name: t("common.api.products.collections.INVERTERS_ACCESSORIES"),
              to: {
                name: "HardwareMarketplaceCollection",
                params: { collection: "inverters" },
              },
            },
          ],
        },
      ],
    },
    batteries: {
      id: "BATTERIES",
      sections: [
        {
          name: t("common.shop.hardware.navigation.primary.manufacturers"),
          items: featuredManufacturers.value
            .filter((o) => o.hasBatteries)
            .map((o) => {
              return {
                name: o.companyName,
                to: {
                  name: "HardwareMarketplaceCollectionManufacturer",

                  params: { collection: "batteries", manufacturerId: o.slug },
                },
              }
            }),
        },
      ],
    },
    mounting_systems: {
      id: "MOUNTING_SYSTEMS",
      sections: [
        {
          name: t("common.shop.hardware.navigation.primary.manufacturers"),
          items: featuredManufacturers.value
            .filter((o) => o.hasBos)
            .map((o) => {
              return {
                name: o.companyName,
                to: {
                  name: "HardwareMarketplaceCollectionManufacturer",

                  params: {
                    collection: "mounting-systems",
                    manufacturerId: o.slug,
                  },
                },
              }
            }),
        },
      ],
    },
  }
})

function buildPrimaryNavigation() {
  return [
    {
      id: "categories",
      name: t("common.shop.hardware.navigation.primary.categories"),
      open: false,
      collections: [
        {
          name: t("common.api.products.collections.PANELS"),
          item: collections.value.panels.sections,
        },
        {
          name: t("common.api.products.collections.INVERTERS"),
          item: collections.value.inverters.sections,
        },
        {
          name: t("common.api.products.collections.BATTERIES"),
          item: collections.value.batteries.sections,
        },
        {
          name: t("common.api.products.collections.MOUNTING_SYSTEMS"),
          item: collections.value.mounting_systems.sections,
        },
      ],
    },
    {
      id: "panels",
      name: t("common.api.products.collections.PANELS"),
      open: false,
      sections: collections.value.panels.sections,
    },
    {
      id: "inverters",
      name: t("common.api.products.collections.INVERTERS"),
      open: false,
      sections: collections.value.inverters.sections,
    },
    {
      id: "batteries",
      name: t("common.api.products.collections.BATTERIES"),
      open: false,
      sections: collections.value.batteries.sections,
    },
    {
      id: "mounting-systems",
      name: t("common.api.products.collections.MOUNTING_SYSTEMS"),
      open: false,
      sections: collections.value.mounting_systems.sections,
    },
  ]
}

const secondaryNavigation = [
  {
    name: t("common.shop.hardware.navigation.secondary.products"),
    to: { name: "HardwareMarketplaceShopManufacturers" },
  },
  {
    name: t("common.shop.hardware.navigation.secondary.manufacturers"),
    to: { name: "HardwareMarketplaceShopManufacturers" },
  },
  {
    name: t("common.shop.hardware.navigation.secondary.suppliers"),
    to: { name: "HardwareMarketplaceShopSuppliers" },
  },
]

function openPopover(index) {
  clearTimeout(primaryNavigation.value[index].closeTimeout)
  primaryNavigation.value[index].open = true
}
function closePopover(index) {
  primaryNavigation.value[index].closeTimeout = setTimeout(() => {
    primaryNavigation.value[index].open = false
  }, 200) // Add a small delay for smoother interaction
}
function clearCloseTimeout(index) {
  clearTimeout(primaryNavigation.value[index].closeTimeout)
}
</script>

<template>
  <header
    class="fixed w-full z-30 md:bg-opacity-90 transition duration-300 ease-in-out"
    :class="{ 'bg-white backdrop-blur-sm shadow-lg': !top }"
  >
    <Banner />
    <nav class="flex items-center justify-between px-20 py-3 bg-white shadow">
      <!-- Left: Logo + SearchBar (1/2 width) -->
      <div class="flex items-center w-1/2 min-w-0">
        <div class="flex flex-col">
          <div class="flex items-end mr-4">
            <RouterLink to="/">
              <h2
                :class="dark && top ? 'text-gray-200' : 'text-gray-800'"
                class="hidden md:block h2 text-xl font-extrabold leading-tight tracking-tighter mr-2"
              >
                {{ $t("common.shop.hardware.navigation.title") }}
              </h2>
            </RouterLink>
          </div>
        </div>
        <div class="hidden md:block flex-1 min-w-0">
          <HardwareProductSearch />
        </div>
      </div>
      <!-- Right: Language + Buttons (1/2 width, right-aligned) -->
      <div class="flex items-center justify-end w-1/2 space-x-4 ml-6">
        <button
          :class="
            dark && top
              ? 'text-gray-200 hover:text-gray-400'
              : 'text-gray-600 hover:text-gray-900'
          "
          class="rounded-lg px-8 py-3 flex items-center transition duration-150 ease-in-out ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          @click="$emit('sign-in')"
        >
          {{ $t("common.sign-in") }}
        </button>
        <router-link
          :to="{ name: 'InstallerHome' }"
          class="rounded-lg px-8 py-3 text-white hover:bg-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 bg-gradient-to-r bg-gray-900 ml-3"
        >
          {{ $t("common.buttons.back") }}
        </router-link>
      </div>
    </nav>
    <div
      class="container-fluid border-b border-gray-200 bg-white py-2 px-4 block xl:hidden"
    >
      <HardwareProductSearch />
    </div>
    <!-- secondary menu -->
    <SecondaryMenubar :primary-navigation="primaryNavigation" />
  </header>
</template>
